import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface Props { rows: RawIndicatorRow[] }

const PURVIEW_PURPLE = "#742774";
const PURVIEW_BG = "#FAF5FF";
const PURVIEW_BORDER = "#CE93D8";

const toNum = (v: string | number | undefined): number | null => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const fmtInt = (n: number | null): string =>
  n === null ? "—" : Math.round(n).toLocaleString();

interface ParsedData {
  productAbbr: string;
  mdpu: number | null;
  mpu: number | null;
  engagementRate: number | null;
}

function parse(rows: RawIndicatorRow[]): ParsedData {
  let mdpu: number | null = null;
  let mpu: number | null = null;
  let engagementRate: number | null = null;
  const productAbbr = rows[0]?.sectionName ?? "—";

  for (const row of rows) {
    const lbl = (row.factorLabel ?? "").trim();
    if (/Monthly Deeply Protected Users \(MDPU\)/i.test(lbl)) mdpu = toNum(row.value);
    else if (/Monthly Protected Users \(MPU\)/i.test(lbl)) mpu = toNum(row.value);
    else if (/Engag.*Rate/i.test(lbl)) engagementRate = toNum(row.value);
  }
  return { productAbbr, mdpu, mpu, engagementRate };
}

interface FactorBoxProps { label: string; value: string; valueColor?: string }
const FactorBox: React.FC<FactorBoxProps> = ({ label, value, valueColor = "#323130" }) => (
  <Stack
    horizontalAlign="center"
    tokens={{ childrenGap: 2 }}
    styles={{
      root: {
        backgroundColor: "#FFFFFF",
        border: "1px solid #E8D5F0",
        borderRadius: 8,
        padding: "8px 10px",
        flex: "1 1 0",
        textAlign: "center",
        minWidth: 80,
      },
    }}
  >
    <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>{label}</Text>
    <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: valueColor } }}>{value}</Text>
  </Stack>
);

const PurviewEngagementRawIndicatorCards: React.FC<Props> = ({ rows }) => {
  const data = useMemo(() => parse(rows), [rows]);

  const isCapped = data.engagementRate !== null && data.engagementRate > 100;
  const isNull = data.engagementRate === null && data.mpu === null;
  const displayRate = data.engagementRate === null
    ? "NULL"
    : isCapped
      ? `${data.engagementRate?.toFixed(1)}% → 100`
      : `${data.engagementRate?.toFixed(2)}%`;
  const rateColor = data.engagementRate === null
    ? "#A19F9D"
    : isCapped
      ? "#C15A00"
      : data.engagementRate >= 80
        ? "#107C10"
        : data.engagementRate >= 50
          ? "#FFB900"
          : "#D13438";

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          backgroundColor: PURVIEW_BG,
          border: `1.5px solid ${PURVIEW_BORDER}`,
          borderRadius: 12,
          padding: "20px 24px",
          maxWidth: 440,
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="large" styles={{ root: { fontWeight: 700, color: PURVIEW_PURPLE } }}>
          {data.productAbbr}
        </Text>
        {isCapped && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{ root: { backgroundColor: "#FFF3E0", borderRadius: 12, padding: "2px 10px", border: "1px solid #E65100" } }}
          >
            <Text variant="xSmall" styles={{ root: { fontWeight: 700, color: "#E65100" } }}>CAPPED</Text>
          </Stack>
        )}
        {isNull && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{ root: { backgroundColor: "#EEEEEE", borderRadius: 12, padding: "2px 10px" } }}
          >
            <Text variant="xSmall" styles={{ root: { fontWeight: 700, color: "#757575" } }}>NULL</Text>
          </Stack>
        )}
      </Stack>

      {/* Factor boxes */}
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <FactorBox label="MDPU" value={fmtInt(data.mdpu)} />
        <FactorBox label="MPU" value={fmtInt(data.mpu)} />
        <FactorBox label="Engagement Rate" value={displayRate} valueColor={rateColor} />
      </Stack>
    </Stack>
  );
};

export default PurviewEngagementRawIndicatorCards;
